import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";

/* /superuser — Neo's control room. Admin login + Neo's passcode. */

const Panel = ({ title, testid, children, right }) => (
  <section data-testid={testid} className="border border-yt-border bg-black/60 p-5">
    <div className="flex items-center gap-3 mb-4">
      <h2 className="font-mono text-[11px] tracking-[0.3em] text-yt-judge">{title}</h2>
      <span className="flex-1 h-px bg-yt-border" />
      {right}
    </div>
    {children}
  </section>
);

const Btn = ({ children, onClick, testid, tone = "plain", disabled }) => {
  const tones = {
    plain: "border-white/30 hover:border-white text-white",
    go: "border-yt-judge text-yt-judge hover:bg-yt-judge hover:text-black",
    warn: "border-yt-yellow text-yt-yellow hover:bg-yt-yellow hover:text-black",
    stop: "border-yt-red text-yt-red hover:bg-yt-red hover:text-white",
  };
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
            className={`px-3 py-2 border font-mono text-[11px] tracking-widest transition-colors disabled:opacity-40 ${tones[tone]}`}>
      {children}
    </button>
  );
};

function Unlock({ onDone }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try { await axios.post(`${API}/superuser/unlock`, { passcode: code }); onDone(); }
    catch (e2) { setErr(e2?.response?.data?.detail || "couldn't unlock"); }
  };
  return (
    <form onSubmit={submit} data-testid="su-unlock-form" className="max-w-md mx-auto mt-24 border border-yt-border bg-black/70 p-8">
      <div className="font-mono text-[10px] tracking-[0.4em] text-yt-judge mb-3">NEO · CONTROL ROOM</div>
      <h1 className="font-display text-4xl mb-6">SUPERUSER</h1>
      <input data-testid="su-passcode-input" type="password" value={code} onChange={e => setCode(e.target.value)}
             placeholder="Neo's passcode" autoFocus
             className="w-full bg-black border border-white/30 px-3 py-3 font-mono text-sm focus:outline-none focus:border-yt-judge" />
      {err && <div data-testid="su-unlock-error" className="mt-3 font-mono text-xs text-yt-red">{err}</div>}
      <button data-testid="su-unlock-btn" className="mt-4 w-full bg-yt-judge text-black font-display tracking-wider py-3 hover:bg-white transition-colors">UNLOCK</button>
    </form>
  );
}

function LoopPanel({ loop, act }) {
  const color = { running: "bg-yt-judge", paused: "bg-yt-yellow", stopped: "bg-yt-red" }[loop.mode] || "bg-[#f2b06b]";
  return (
    <Panel title="THE PULSE · NEO'S LOOP" testid="su-loop-panel">
      <div className="flex items-center gap-3">
        <span className={`w-3 h-3 rounded-full ${color} ${loop.mode === "running" ? "animate-pulse" : ""}`} />
        <span data-testid="su-loop-mode" className="font-display text-3xl uppercase">{loop.mode}</span>
      </div>
      <div className="mt-2 font-mono text-[11px] text-white/60 space-y-1">
        <div data-testid="su-loop-pulses">pulses: {loop.pulses} · every {loop.interval}s · songs checked: {loop.qc_checked}</div>
        <div>last pulse: {loop.last_pulse_at?.slice(11, 19) || "—"} UTC · last step: {loop.last_step || "—"}</div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {loop.mode === "paused"
          ? <Btn testid="su-loop-resume" tone="go" onClick={() => act("resume")}>RESUME</Btn>
          : <Btn testid="su-loop-pause" tone="warn" onClick={() => act("pause")} disabled={loop.mode === "stopped"}>PAUSE</Btn>}
        {loop.mode === "stopped"
          ? <Btn testid="su-loop-start" tone="go" onClick={() => act("start")}>START LOOP</Btn>
          : <Btn testid="su-loop-stop" tone="stop" onClick={() => act("stop")}>STOP LOOP</Btn>}
      </div>
    </Panel>
  );
}

function HealthPanel({ loop, metrics }) {
  return (
    <Panel title="HEALTH" testid="su-health-panel" right={<span className="font-mono text-[10px] text-white/40">{loop.health_at?.slice(11, 19)} UTC</span>}>
      <div className="grid grid-cols-5 gap-2 mb-4">
        {Object.entries(metrics).map(([k, v]) => (
          <div key={k} data-testid={`su-metric-${k}`} className="border border-white/10 p-2">
            <div className="font-display text-2xl text-yt-yellow">{v}</div>
            <div className="font-mono text-[9px] tracking-widest text-white/50 uppercase">{k.replace(/_/g, " ")}</div>
          </div>
        ))}
      </div>
      <ul className="font-mono text-xs space-y-1">
        {loop.health.map((h, i) => (
          <li key={i} data-testid={`su-health-${i}`} className="flex gap-3">
            <span className={h.status === 200 ? "text-yt-judge" : "text-yt-red"}>{h.status === 200 ? "OK " : "BAD"}</span>
            <span className="flex-1 text-white/80">{h.route}</span>
            <span className="text-white/40">{h.status} · {h.ms}ms</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Console({ onReply }) {
  const sid = "superuser-console";
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    axios.get(`${API}/uncle-neo/history`, { params: { session_id: sid, limit: 40 } })
      .then(r => setTurns(r.data?.turns || [])).catch(() => {});
  }, []);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [turns, busy]);
  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setBusy(true); setInput("");
    setTurns(t => [...t, { role: "user", content: msg }]);
    try {
      const r = await axios.post(`${API}/uncle-neo/chat`, { message: msg, session_id: sid });
      setTurns(t => [...t, { role: "assistant", content: r.data?.reply || "" }]);
    } catch (e) {
      setTurns(t => [...t, { role: "assistant", content: e?.response?.data?.detail || "couldn't reach Neo" }]);
    }
    setBusy(false); onReply();
  };
  return (
    <Panel title="CONSOLE · TALK TO NEO INSIDE THE LOOP" testid="su-console">
      <div ref={ref} data-testid="su-console-transcript" className="h-80 overflow-y-auto space-y-3 font-mono text-sm pr-2">
        {turns.length === 0 && <div className="text-white/40 text-xs">Type STOP LOOP or START LOOP any time. Anything else, Neo answers while the loop waits.</div>}
        {turns.map((t, i) => (
          <div key={i} data-testid={`su-console-turn-${i}`} className={t.role === "user" ? "text-white" : "text-[#f2d8a8] whitespace-pre-wrap"}>
            <div className="text-[9px] tracking-widest text-white/40">{t.role === "user" ? "PAUL" : "NEO"}</div>{t.content}
          </div>
        ))}
        {busy && <div data-testid="su-console-typing" className="text-xs text-white/50">neo is answering, the loop is waiting…</div>}
      </div>
      <div className="mt-3 flex gap-2">
        <textarea data-testid="su-console-input" rows={2} value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  className="flex-1 bg-black border border-white/30 px-3 py-2 font-mono text-sm resize-none focus:outline-none focus:border-yt-judge" />
        <Btn testid="su-console-send" tone="go" onClick={send} disabled={busy || !input.trim()}>SEND</Btn>
      </div>
    </Panel>
  );
}

function MemoryPanel({ memory }) {
  const [tab, setTab] = useState(0);
  const doc = memory[tab];
  return (
    <Panel title="MEMORY INSPECTOR" testid="su-memory-panel">
      <div className="flex gap-2 mb-3">
        {memory.map((m, i) => (
          <button key={m.name} data-testid={`su-memory-tab-${m.name}`} onClick={() => setTab(i)}
                  className={`px-3 py-1.5 font-mono text-[11px] border transition-colors ${i === tab ? "border-yt-judge text-yt-judge" : "border-white/20 text-white/60 hover:text-white"}`}>
            {m.name} <span className={m.lines > 100 ? "text-yt-red" : "text-white/40"}>{m.lines}/100</span>
          </button>
        ))}
      </div>
      <pre data-testid="su-memory-content" className="h-96 overflow-y-auto whitespace-pre-wrap font-mono text-xs text-white/80 bg-black/50 p-3 border border-white/10">{doc?.content}</pre>
    </Panel>
  );
}

const TOGGLES = [["lyric_qc", "LYRIC QC"], ["auto_fix", "AUTO-FIX ON ROUNDS"], ["submissions_open", "SONG SUBMISSIONS OPEN"], ["hand_auto_approve", "HAND TYPES WITHOUT ASKING"], ["hand_neo_verifies", "NEO APPROVES HAND JOBS WHEN I'M AWAY"], ["awake_loop", "NEO THINKS ON HIS OWN"]];
const NUMBERS = [["loop_interval_sec", "LOOP INTERVAL (s)"], ["min_unique_words", "MIN UNIQUE WORDS"], ["low_quality_score", "LOW-QUALITY SCORE"], ["hand_paul_window_min", "MINUTES BEFORE NEO DECIDES"], ["daily_budget_usd", "DAILY BUDGET ($)"], ["awake_gap_min", "MIN MINUTES BETWEEN WAKES"], ["awake_quiet_max_h", "WAKE AFTER QUIET (HOURS)"]];

function Controls({ settings, save, reload }) {
  const [draft, setDraft] = useState(settings);
  const [terms, setTerms] = useState((settings.extra_blocked_terms || []).join(", "));
  const [msg, setMsg] = useState("");
  const saved = JSON.stringify(settings);
  useEffect(() => {
    const s = JSON.parse(saved);
    setDraft(s);
    setTerms((s.extra_blocked_terms || []).join(", "));
  }, [saved]);
  const run = async (path, label) => { if (await save(null, path)) { setMsg(`${label} started`); setTimeout(reload, 2500); } };
  return (
    <Panel title="CONTROLS" testid="su-controls">
      <div className="space-y-2">
        {TOGGLES.map(([k, label]) => (
          <button key={k} data-testid={`su-toggle-${k}`} onClick={() => save({ [k]: !settings[k] })}
                  className="w-full flex items-center justify-between border border-white/15 px-3 py-2 font-mono text-xs hover:border-white/40 transition-colors">
            <span>{label}</span>
            <span className={settings[k] ? "text-yt-judge" : "text-yt-red"}>{settings[k] ? "ON" : "OFF"}</span>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        {NUMBERS.map(([k, label]) => (
          <label key={k} className="font-mono text-[9px] tracking-widest text-white/50">{label}
            <input data-testid={`su-number-${k}`} type="number" value={draft[k] ?? ""} onChange={e => setDraft({ ...draft, [k]: e.target.value })}
                   className="mt-1 w-full bg-black border border-white/30 px-2 py-1.5 text-sm text-white" />
          </label>
        ))}
      </div>
      <label className="block mt-3 font-mono text-[9px] tracking-widest text-white/50">EXTRA BLOCKED WORDS (comma separated)
        <textarea data-testid="su-blocked-terms" rows={2} value={terms}
                  onChange={e => setTerms(e.target.value)}
                  className="mt-1 w-full bg-black border border-white/30 px-2 py-1.5 text-sm text-white resize-none" />
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        <Btn testid="su-save-settings" tone="go" onClick={() => save({
          loop_interval_sec: draft.loop_interval_sec, min_unique_words: draft.min_unique_words,
          hand_paul_window_min: draft.hand_paul_window_min, daily_budget_usd: draft.daily_budget_usd,
          awake_gap_min: draft.awake_gap_min, awake_quiet_max_h: draft.awake_quiet_max_h,
          low_quality_score: draft.low_quality_score,
          extra_blocked_terms: terms.split(",").map(t => t.trim()).filter(Boolean),
        })}>SAVE SETTINGS</Btn>
        <Btn testid="su-run-rounds" onClick={() => run("/site-manager/rounds", "Rounds")}>WALK ROUNDS NOW</Btn>
        <Btn testid="su-run-money" onClick={() => run("/site-manager/money-week", "Money note")}>MONEY NOTE NOW</Btn>
      </div>
      {msg && <div data-testid="su-controls-msg" className="mt-2 font-mono text-[11px] text-yt-judge">{msg}</div>}
    </Panel>
  );
}

function SongList({ items, action, label, testid }) {
  if (!items.length) return <div data-testid={`${testid}-empty`} className="font-mono text-xs text-white/40">none</div>;
  return (
    <ul className="space-y-2">
      {items.map(t => (
        <li key={t.id} data-testid={`${testid}-${t.id}`} className="flex items-center gap-3 font-mono text-xs border border-white/10 p-2">
          <Link to={`/tracks/${t.id}`} className="flex-1 hover:text-yt-yellow">{t.title} <span className="text-white/40">· {t.by} · score {t.score}</span>
            <div className="text-white/50">{t.reasons.join("; ")}</div></Link>
          <Btn testid={`${testid}-${action}-${t.id}`} onClick={() => action && label && action(t.id)}>{label}</Btn>
        </li>
      ))}
    </ul>
  );
}

function QCPanel({ held, flagged, trackAct }) {
  return (
    <Panel title="LYRIC QC" testid="su-qc-panel">
      <div className="font-mono text-[10px] tracking-widest text-yt-red mb-2">HELD OFF THE CHARTS</div>
      <SongList items={held} action={(id) => trackAct(id, "release")} label="RELEASE" testid="su-held" />
      <div className="font-mono text-[10px] tracking-widest text-yt-yellow mt-4 mb-2">FLAGGED · STILL LIVE</div>
      <SongList items={flagged} action={(id) => trackAct(id, "hold")} label="HOLD" testid="su-flagged" />
    </Panel>
  );
}

function LogPanel({ directives, duties, round }) {
  return (
    <Panel title="DIRECTIVES · DUTIES · LAST ROUND" testid="su-log-panel">
      <div className="font-mono text-xs text-[#f2d8a8] mb-3" data-testid="su-last-round">{round ? `${round.started_at?.slice(0, 16).replace("T", " ")} · ${round.summary}` : "no rounds yet"}</div>
      <ul className="font-mono text-xs space-y-1 mb-4">
        {duties.open.map(d => <li key={d.id} data-testid={`su-duty-${d.id}`} className="text-white/85"><span className="text-[#f2b06b]">[ ]</span> {d.text}</li>)}
        {duties.open.length === 0 && <li className="text-white/40">duty list empty</li>}
      </ul>
      <ul className="font-mono text-[11px] space-y-1 max-h-64 overflow-y-auto" data-testid="su-directives">
        {directives.map((d, i) => <li key={i} className="text-white/70"><span className="text-white/40">{d.at?.slice(5, 16).replace("T", " ")} · {d.by}</span> — {d.text}</li>)}
        {directives.length === 0 && <li className="text-white/40">no directives logged yet</li>}
      </ul>
    </Panel>
  );
}

function Handoff() {
  const files = [["one_persona.json", "NEO'S MEMORY"], ["local_manager.py", "THE LOOP"], ["README.md", "SETUP STEPS"], ["key_striker.py", "KEY STRIKER"], ["keymap.json", "KEY MAP"], ["hand_agent.py", "HAND AGENT"], ["neo_vault.py", "PASSWORD VAULT"]];
  return (
    <Panel title="RYZEN HANDOFF" testid="su-handoff-panel">
      <p className="font-mono text-xs text-white/60 mb-3">Everything Neo needs to run at home on your own model. Memory is built fresh each time you download.</p>
      <div className="flex flex-wrap gap-2">
        {files.map(([f, label]) => (
          <a key={f} href={`${API}/superuser/package/${f}`} download={f} data-testid={`su-download-${f}`}
             className="px-3 py-2 border border-yt-judge text-yt-judge hover:bg-yt-judge hover:text-black font-mono text-[11px] tracking-widest transition-colors">
            {label} · {f}
          </a>
        ))}
      </div>
    </Panel>
  );
}

function AwakePanel({ awake }) {
  const m = awake.meter;
  const pct = Math.min(100, (m.today / (m.budget || 1)) * 100);
  return (
    <Panel title="NEO AWAKE · COST METER" testid="su-awake-panel" right={
      <span className="font-mono text-[10px] text-white/40">last woke {awake.last_tick_at ? awake.last_tick_at.slice(11, 16) + " UTC" : "not yet"}</span>}>
      <div className="flex items-end gap-3">
        <span data-testid="su-spend-today" className="font-display text-4xl text-yt-yellow">${m.today.toFixed(2)}</span>
        <span className="font-mono text-xs text-white/50 mb-1">of ${m.budget} today · {m.calls_today} calls · estimate</span>
      </div>
      <div className="h-1.5 bg-white/10 mt-2"><div className="h-full bg-yt-judge" style={{ width: `${pct}%` }} /></div>
      <div className="mt-2 font-mono text-[10px] text-white/50">
        {Object.entries(m.by_purpose).map(([k, v]) => <span key={k} className="mr-3">{k} ${v.toFixed(2)}</span>)}
      </div>
      <div className="mt-1 font-mono text-[10px] text-white/30">week: {m.week.map(d => `${d.day.slice(5)} $${d.total.toFixed(2)}`).join(" · ")}</div>
      <div className="mt-4 font-mono text-[10px] tracking-widest text-white/60">HIS MEMO</div>
      <ul className="font-mono text-xs text-white/70">{awake.memo.slice(-5).map((l, i) => <li key={i}>{l}</li>)}{!awake.memo.length && <li className="text-white/40">empty</li>}</ul>
      <div className="mt-4 font-mono text-[10px] tracking-widest text-white/60">WHAT HE THOUGHT AND DID</div>
      <ul data-testid="su-awake-log" className="font-mono text-xs space-y-2 max-h-56 overflow-y-auto">
        {awake.log.map((l, i) => (
          <li key={i} className="text-white/80 whitespace-pre-wrap"><span className="text-white/40">{l.at.slice(5, 16).replace("T", " ")} · {(l.events || []).join("; ") || "quiet spell"}</span>{"\n"}{l.thought}</li>
        ))}
        {!awake.log.length && <li className="text-white/40">hasn't woken on his own yet</li>}
      </ul>
    </Panel>
  );
}

function HandPanel() {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const load = useCallback(() => axios.get(`${API}/hand/jobs`).then(r => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [load]);
  const decide = async (id, action) => { await axios.post(`${API}/hand/jobs/${id}/${action}`).catch(() => {}); load(); };
  const queue = async () => { if (!text.trim()) return; await axios.post(`${API}/hand/jobs`, { text }); setText(""); load(); };
  const seen = data?.device_last_seen;
  const online = seen && Date.now() - new Date(seen).getTime() < 30000;
  const tone = { pending: "text-yt-yellow", approved: "text-[#9ab8d9]", claimed: "text-[#f2b06b]", verifying: "text-[#f2b06b]", done: "text-yt-judge", failed: "text-yt-red", rejected: "text-white/40" };
  return (
    <Panel title="THE HAND" testid="su-hand-panel" right={
      <span data-testid="su-hand-device" className={`font-mono text-[10px] ${online ? "text-yt-judge" : "text-white/40"}`}>
        {online ? "RYZEN ONLINE" : seen ? `ryzen last seen ${seen.slice(11, 16)} UTC` : "ryzen not connected yet"}
      </span>}>
      <div className="flex gap-2 mb-3">
        <input data-testid="su-hand-input" value={text} onChange={e => setText(e.target.value)} placeholder="text for the hand to type"
               className="flex-1 bg-black border border-white/30 px-3 py-2 font-mono text-sm" />
        <Btn testid="su-hand-queue" tone="go" onClick={queue}>QUEUE</Btn>
      </div>
      <div data-testid="su-hand-vault" className="font-mono text-[10px] tracking-widest text-white/50 mb-2">
        VAULT ON THE RYZEN: {(data?.vault || []).join(", ") || "empty"}
      </div>
      <ul className="space-y-1.5 font-mono text-xs max-h-64 overflow-y-auto">
        {(data?.jobs || []).map(j => (
          <li key={j.id} data-testid={`su-hand-job-${j.id}`} className="flex items-center gap-2 border border-white/10 px-2 py-1.5">
            <span className={`w-16 uppercase ${tone[j.status] || ""}`}>{j.status}</span>
            <span className="flex-1 truncate">{j.purpose && <span className="text-white/60">{j.purpose}: </span>}{j.text}
              <span className="text-white/40"> · {j.source}{j.decided_by ? ` · ${j.decided_by}` : ""}</span>
              {j.reason && <span className="block text-white/50 normal-case">{j.reason}</span>}</span>
            {j.status === "pending" && <>
              <Btn testid={`su-hand-approve-${j.id}`} tone="go" onClick={() => decide(j.id, "approve")}>YES</Btn>
              <Btn testid={`su-hand-reject-${j.id}`} tone="stop" onClick={() => decide(j.id, "reject")}>NO</Btn>
            </>}
          </li>
        ))}
        {data && data.jobs.length === 0 && <li className="text-white/40">no jobs yet</li>}
      </ul>
    </Panel>
  );
}

export default function Superuser() {
  const { user, loading } = useAuth();
  const [unlocked, setUnlocked] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/superuser/overview`)).data); setUnlocked(true); }
    catch (e) {
      if (e?.response?.status === 401) setUnlocked(false);
      else setErr(e?.response?.data?.detail || "couldn't reach the control room");
    }
  }, []);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);
  useEffect(() => {
    if (!unlocked) return;
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [unlocked, load]);

  if (loading) return null;
  if (!user || user.role !== "admin") {
    return <div data-testid="su-denied" className="max-w-md mx-auto mt-24 font-mono text-sm text-white/60 text-center">Nothing here.</div>;
  }
  if (unlocked === false) return <Unlock onDone={load} />;
  if (!data) return <div data-testid="su-loading" className="mt-24 text-center font-mono text-xs text-white/40">{err || "opening the control room…"}</div>;

  const call = async (fn) => {
    setErr("");
    try { await fn(); await load(); return true; }
    catch (e) {
      if (e?.response?.status === 401) { setUnlocked(false); setData(null); }
      else setErr(e?.response?.data?.detail || "that didn't go through");
      return false;
    }
  };
  const act = (action) => call(() => axios.post(`${API}/superuser/loop`, { action }));
  const save = (patch, postPath) => call(() => (postPath ? axios.post(`${API}${postPath}`) : axios.put(`${API}/superuser/settings`, patch)));
  const trackAct = (id, action) => call(() => axios.post(`${API}/superuser/tracks/${id}/${action}`));
  const lock = async () => { await axios.post(`${API}/superuser/lock`).catch(() => {}); setUnlocked(false); setData(null); };

  return (
    <div data-testid="superuser-page" className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <div className="flex items-end gap-4 mb-8">
        <div>
          <div className="font-mono text-[10px] tracking-[0.4em] text-yt-judge">NEO · CONTROL ROOM</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none mt-2">SUPERUSER</h1>
        </div>
        <span className="flex-1" />
        <Btn testid="su-lock-btn" onClick={lock}>LOCK</Btn>
      </div>
      {err && <div data-testid="su-error" className="mb-5 border border-yt-red text-yt-red font-mono text-xs p-3">{err}</div>}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="space-y-5">
          <LoopPanel loop={data.loop} act={act} />
          <AwakePanel awake={data.awake} />
          <Controls settings={data.settings} save={save} reload={load} />
        </div>
        <div className="lg:col-span-2 space-y-5">
          <Console onReply={load} />
          <HealthPanel loop={data.loop} metrics={data.metrics} />
        </div>
        <div className="lg:col-span-2 space-y-5"><MemoryPanel memory={data.memory} /><HandPanel /><Handoff /></div>
        <div className="space-y-5">
          <QCPanel held={data.held} flagged={data.flagged} trackAct={trackAct} />
          <LogPanel directives={data.directives} duties={data.duties} round={data.latest_round} />
        </div>
      </div>
    </div>
  );
}
